import { useCallback } from "react";

// @ts-expect-error – no type declarations for save-svg-as-png
import { saveSvgAsPng } from "save-svg-as-png";
import workSansFontUrl from "@/assets/fonts/WorkSans-VariableFont_wght.ttf?url";
import mcQueenGroteskFontUrl from "@/assets/fonts/McQueenGrotesk-RegularWeb.woff2?url";

const EXPORT_FONTS = [
  {
    text: `@font-face { font-family: "Work Sans"; src: url("${workSansFontUrl}") format("truetype"); }`,
    url: workSansFontUrl,
    format: "application/x-font-ttf",
  },
  {
    text: `@font-face { font-family: "McQueen Grotesk"; src: url("${mcQueenGroteskFontUrl}") format("woff2"); }`,
    url: mcQueenGroteskFontUrl,
    format: "font/woff2",
  },
];

const getPlotSvg = (plotClassName: string, root: ParentNode) => {
  return root.querySelector<SVGSVGElement>(`svg.${plotClassName}.svg`);
};

const getPlotLegend = (plotClassName: string, root: ParentNode) => {
  return root.querySelector<HTMLElement>(
    `.plot-container.${plotClassName} [data-plot-legend]`,
  );
};

export const getPlotFileName = (name?: string) => {
  const date = new Date();
  const formattedDate = date.toISOString().split("T")[0].replaceAll("-", "");
  return `${formattedDate}_${name || ""}.png`;
};

const waitForFonts = async () => {
  if ("fonts" in document) {
    await document.fonts.ready;
  }
};

const getSvgDimensions = (plot: SVGSVGElement) => {
  const viewBox = plot.viewBox?.baseVal;
  if (viewBox && viewBox.width > 0 && viewBox.height > 0) {
    return {
      width: viewBox.width,
      height: viewBox.height,
      minX: viewBox.x,
      minY: viewBox.y,
    };
  }

  const rect = plot.getBoundingClientRect();
  return {
    width: rect.width,
    height: rect.height,
    minX: 0,
    minY: 0,
  };
};

// Matches the UI legend's max-w-20 (80px) label box in Legend.tsx
const MAX_LEGEND_LABEL_WIDTH = 80;
const LEGEND_FONT = "12px Work Sans";

const measureLabelWidth = (text: string) => {
  const context = document.createElement("canvas").getContext("2d");
  if (!context) return text.length * 7;
  context.font = LEGEND_FONT;
  return context.measureText(text).width;
};

const truncateLabel = (text: string, maxWidth: number) => {
  if (measureLabelWidth(text) <= maxWidth) return text;

  let truncated = text;
  while (truncated.length && measureLabelWidth(`${truncated}…`) > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return `${truncated}…`;
};

const appendLegendToSvgClone = (
  svgClone: SVGSVGElement,
  legend: HTMLElement | null,
) => {
  if (!legend) return svgClone;

  const items = Array.from(legend.querySelectorAll("[data-plot-legend-item]"));
  if (!items.length) return svgClone;

  const { width, height, minX, minY } = getSvgDimensions(svgClone);
  const legendMargin = 24;
  const rowHeight = 22;
  const availableWidth = width - legendMargin * 2;

  const entries = items.flatMap((item) => {
    const swatch = item.querySelector("[data-plot-legend-swatch]");
    const label = item.querySelector("[data-plot-legend-label]");
    if (!swatch || !label) return [];

    const swatchStyle = window.getComputedStyle(swatch);
    const labelText = truncateLabel(
      label.textContent?.trim() || "",
      MAX_LEGEND_LABEL_WIDTH,
    );
    const labelWidth = Math.min(
      MAX_LEGEND_LABEL_WIDTH,
      measureLabelWidth(labelText),
    );
    return [{ swatchStyle, labelText, itemWidth: 28 + labelWidth }];
  });

  let xOffset = 0;
  let row = 0;
  const positioned = entries.map((entry) => {
    if (xOffset > 0 && xOffset + entry.itemWidth > availableWidth) {
      row += 1;
      xOffset = 0;
    }
    const position = { ...entry, x: xOffset, y: row * rowHeight };
    xOffset += entry.itemWidth;
    return position;
  });

  const legendHeight = (row + 1) * rowHeight + 14;
  const totalHeight = height + legendHeight;

  svgClone.setAttribute("viewBox", `${minX} ${minY} ${width} ${totalHeight}`);
  svgClone.setAttribute("height", `${totalHeight}`);

  const svgNs = "http://www.w3.org/2000/svg";
  const legendGroup = document.createElementNS(svgNs, "g");
  (
    legendGroup as SVGGElement & { dataset: DOMStringMap }
  ).dataset.exportLegend = "true";
  legendGroup.setAttribute(
    "transform",
    `translate(${legendMargin}, ${height + 10})`,
  );

  positioned.forEach(({ swatchStyle, labelText, x, y }) => {
    const rect = document.createElementNS(svgNs, "rect");
    rect.setAttribute("x", `${x}`);
    rect.setAttribute("y", `${y}`);
    rect.setAttribute("width", "14");
    rect.setAttribute("height", "14");
    rect.setAttribute("rx", "2");
    rect.setAttribute("fill", swatchStyle.backgroundColor || "#D2D4D5");
    rect.setAttribute("stroke", swatchStyle.borderColor || "transparent");
    legendGroup.appendChild(rect);

    const text = document.createElementNS(svgNs, "text");
    text.setAttribute("x", `${x + 20}`);
    text.setAttribute("y", `${y + 11}`);
    text.setAttribute("fill", "#2B4752");
    text.setAttribute("font-family", "Work Sans");
    text.setAttribute("font-size", "12");
    text.textContent = labelText;
    legendGroup.appendChild(text);
  });

  svgClone.appendChild(legendGroup);
  return svgClone;
};

const exportPlotSvgAsPng = async (
  plot: SVGSVGElement,
  filename: string,
  root: ParentNode,
) => {
  await waitForFonts();

  const plotClassName = Array.from(plot.classList).find(
    (className) =>
      className !== "svg" && className !== "w-full" && className !== "h-full",
  );
  const legend = plotClassName ? getPlotLegend(plotClassName, root) : null;
  const svgClone = appendLegendToSvgClone(
    plot.cloneNode(true) as SVGSVGElement,
    legend as HTMLElement | null,
  );

  await saveSvgAsPng(svgClone, filename, {
    fonts: EXPORT_FONTS,
    backgroundColor: "#ffffff",
  });
};

export const exportPlot = async (
  plotClassName: string,
  name?: string,
  root: ParentNode = document,
) => {
  const plot = getPlotSvg(plotClassName, root);
  if (!plot) return;

  const filename = getPlotFileName(name);
  await exportPlotSvgAsPng(plot, filename, root);
};

export const usePlotExport = () => {
  return useCallback(exportPlot, []);
};
